import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import IconSvg, { Icons } from 'src/assets/icons/IconSvg';
import SortByMenu from 'src/components/dropdowns/SortByMenu';
import RaffleFilterMenu from 'src/components/dropdowns/RaffleFilterMenu';
import RaffleSearchInput from 'src/components/input/RaffleSearchInput';
import RaffleCard from 'src/components/cards/RaffleCard';
import Pagination from 'src/components/common/Pagination';
import RafflesTable from './components/RafflesTable';
import { useViewport } from 'src/context/ViewportContext';
import { useNavigate } from 'react-router-dom';
import { useRaffles } from 'src/hooks/useRaffles';
import { useRaffleActions } from 'src/hooks/useRaffleActions';
import { useAuth } from 'src/hooks/useAuth';
import { useWallet } from 'src/hooks/useWallet';
import { useNotification } from 'src/hooks/useNotification';
import { appConfig } from 'src/config/appConfig';
import { mapSortToApiParams } from 'src/utils/sortMapping';
import {
  getRaffleDisplayName,
  getRaffleImage,
  getRaffleCreator,
  formatTicketsSold,
  satsToBtc,
} from 'src/utils/raffleHelpers';
import type { RaffleSortOption, RaffleFilterState } from 'src/types/raffleFilters';
import { DEFAULT_FILTER_STATE } from 'src/types/raffleFilters';
import type { RaffleQueryParams, RaffleFavorite } from 'src/types/raffle';

const tabs = [
  { key: 'ordinals', label: 'Ordinals' },
  { key: 'runes', label: 'Runes' },
];

const RaffleList = () => {
  const [activeTab, setActiveTab] = useState('ordinals');
  const [activeView, setActiveView] = useState('grid');
  const { isMobile } = useViewport();
  const navigate = useNavigate();
  const { buyTickets, toggleFavorite } = useRaffleActions();
  const { isConnected, signMessage } = useWallet();
  const { userId, accessToken } = useAuth();
  const notify = useNotification();
  const [favoritedIds, setFavoritedIds] = useState<Set<string>>(new Set());
  const [buyingId, setBuyingId] = useState<string | null>(null);

  // Sort, filter, search, pagination state
  const [selectedSort, setSelectedSort] = useState<RaffleSortOption>('default');
  const [filterState, setFilterState] = useState<RaffleFilterState>(DEFAULT_FILTER_STATE);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const { raffles, meta, isLoading, fetchRaffles } = useRaffles();

  // Fetch user's existing favorites to pre-populate favoritedIds
  useEffect(() => {
    if (!userId || !accessToken) return;
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'X-Api-Key': process.env.REACT_APP_API_KEY as string,
    };
    axios
      .get(`${appConfig.serviceUrl}/users/${userId}/raffles/favorites?limit=1000`, { headers })
      .then(res => {
        const favs: RaffleFavorite[] = res.data?.data || [];
        setFavoritedIds(new Set(favs.map(f => f.raffleId)));
      })
      .catch(() => { /* ignore */ });
  }, [userId, accessToken]);

  const fetchData = useCallback(() => {
    const sortParams = mapSortToApiParams(selectedSort);
    const params: RaffleQueryParams = {
      prizeType: activeTab === 'ordinals' ? 'ORDINALS' : 'RUNES',
      page: currentPage,
      limit: 20,
      ...sortParams,
    };

    // Determine status from filter
    if (filterState.raffleType === 'past') {
      params.status = 'ENDED';
    } else if (filterState.raffleType === 'live') {
      params.status = 'LIVE';
    } else {
      params.status = 'LIVE';
    }

    if (searchQuery.trim()) params.search = searchQuery.trim();
    if (filterState.ticketPriceMin !== null) params.ticketPriceMin = filterState.ticketPriceMin;
    if (filterState.ticketPriceMax !== null) params.ticketPriceMax = filterState.ticketPriceMax;
    if (filterState.endDateWithin) params.endsWithin = filterState.endDateWithin;

    fetchRaffles(params);
  }, [activeTab, selectedSort, filterState, searchQuery, currentPage, fetchRaffles]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBuy = useCallback(
    async (raffleId: string, quantity: number) => {
      if (!isConnected) {
        notify('Please connect your wallet first', 'warning');
        return;
      }
      setBuyingId(raffleId);
      const sig = await signMessage(`Buy ${quantity} ticket(s) for raffle ${raffleId}`);
      if (!sig) {
        setBuyingId(null);
        return;
      }
      const success = await buyTickets(raffleId, quantity);
      setBuyingId(null);
      if (success) {
        notify('Tickets purchased successfully!', 'success');
        fetchData();
      } else {
        notify('Failed to purchase tickets', 'error');
      }
    },
    [isConnected, signMessage, buyTickets, notify, fetchData],
  );

  const handleToggleFavorite = useCallback(
    async (raffleId: string) => {
      if (!isConnected) {
        notify('Please connect your wallet first', 'warning');
        return;
      }
      const result = await toggleFavorite(raffleId);
      if (result) {
        setFavoritedIds(prev => {
          const next = new Set(prev);
          if (result.favorited) {
            next.add(raffleId);
          } else {
            next.delete(raffleId);
          }
          return next;
        });
      }
    },
    [isConnected, toggleFavorite, notify],
  );

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleViewClick = (view: string) => {
    setActiveView(view);
  };

  const handleSortChange = (sort: RaffleSortOption) => {
    setSelectedSort(sort);
    setCurrentPage(1);
  };

  const handleApplyFilters = (filters: RaffleFilterState) => {
    setFilterState(filters);
    setCurrentPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const clearFilter = (key: 'ticketPriceMin' | 'ticketPriceMax' | 'endDateWithin') => {
    setFilterState(prev => ({
      ...prev,
      [key]: key === 'endDateWithin' ? '' : null,
    }));
    setCurrentPage(1);
  };

  const hasActiveFilters =
    filterState.ticketPriceMin !== null ||
    filterState.ticketPriceMax !== null ||
    filterState.endDateWithin !== '';

  const views = [
    { key: 'grid', icon: isMobile ? 'gridS' : 'gridM' },
    { key: 'list', icon: isMobile ? 'listS' : 'listM' },
  ];

  return (
    <>
      <div className="mb-8 space-y-3">
        <div className="flex flex-col gap-y-3 justify-between items-center mb-3 xl:mb-8 xl:flex-row">
          <div className="flex gap-12 justify-between items-center w-full xl:w-fit">
            <h3 className="text-brand-magnolia">Raffles</h3>

            <div className="flex text-sm font-medium rounded-md xl:text-lg bg-grayscale-900 text-grayscale-500">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  className={`px-3 xl:px-4 xl:h-[42px] h-[34px] flex items-center border border-transparent transition-all duration-200 ${
                    activeTab === tab.key
                      ? '!border-brand-tangerine bg-brand-black text-brand-magnolia rounded-md'
                      : ''
                  }`}
                  onClick={() => handleTabClick(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2 justify-between items-center w-full xl:gap-3 xl:w-fit">
            <div className="flex rounded-md bg-grayscale-900 w-fit">
              {views.map(view => (
                <button
                  key={view.key}
                  className={`p-2.5 flex items-center justify-center transition-all duration-200 w-fit ${
                    activeView === view.key
                      ? 'bg-grayscale-800 text-brand-tangerine rounded-md'
                      : 'text-grayscale-500'
                  }`}
                  onClick={() => handleViewClick(view.key)}
                >
                  <IconSvg icon={view.icon as Icons} />
                </button>
              ))}
            </div>

            <div className="flex gap-2 items-center xl:gap-3">
              <div className="hidden sm:block">
                <RaffleSearchInput
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-[250px]"
                />
              </div>

              <SortByMenu selectedSort={selectedSort} onSortChange={handleSortChange} />

              <RaffleFilterMenu filterState={filterState} onApplyFilters={handleApplyFilters} />
            </div>
          </div>

          <div className="block w-full sm:hidden">
            <RaffleSearchInput value={searchQuery} onChange={handleSearchChange} />
          </div>
        </div>

        {hasActiveFilters && (
          <div className="flex gap-3.5 items-center justify-end flex-wrap">
            {filterState.ticketPriceMin !== null && (
              <button
                className="py-2.5 px-4 rounded-lg h-[34px] text-sm font-semibold text-brand-magnolia flex items-center gap-2 bg-grayscale-600"
                onClick={() => clearFilter('ticketPriceMin')}
              >
                <span>Min: {filterState.ticketPriceMin} sats</span>
                <IconSvg icon="closeS" />
              </button>
            )}

            {filterState.ticketPriceMax !== null && (
              <button
                className="py-2.5 px-4 rounded-lg h-[34px] text-sm font-semibold text-brand-magnolia flex items-center gap-2 bg-grayscale-600"
                onClick={() => clearFilter('ticketPriceMax')}
              >
                <span>Max: {filterState.ticketPriceMax} sats</span>
                <IconSvg icon="closeS" />
              </button>
            )}

            {filterState.endDateWithin && (
              <button
                className="py-2.5 px-4 rounded-lg h-[34px] text-sm font-semibold text-brand-magnolia flex items-center gap-2 bg-grayscale-600"
                onClick={() => clearFilter('endDateWithin')}
              >
                <span>Ends in: {filterState.endDateWithin}</span>
                <IconSvg icon="closeS" />
              </button>
            )}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="text-center text-grayscale-400 py-10">Loading raffles...</div>
      ) : raffles.length === 0 ? (
        <div className="text-center text-grayscale-400 py-10">No raffles found</div>
      ) : (
        <>
          {activeView === 'list' && (
            <RafflesTable
              raffles={raffles}
              favoritedIds={favoritedIds}
              onToggleFavorite={handleToggleFavorite}
            />
          )}

          {activeView === 'grid' && (
            <div className="grid gap-3 xl:gap-[18px] grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {raffles.map(raffle => (
                <RaffleCard
                  key={raffle.id}
                  image={getRaffleImage(raffle)}
                  title={getRaffleDisplayName(raffle)}
                  creator={getRaffleCreator(raffle)}
                  ticketsSold={formatTicketsSold(raffle)}
                  ticketsPrice={satsToBtc(raffle.ticketPriceSats)}
                  endDate={raffle.endDate}
                  onClick={() => navigate(`/raffle/${raffle.id}`)}
                  isFavorited={favoritedIds.has(raffle.id)}
                  onToggleFavorite={() => handleToggleFavorite(raffle.id)}
                  onBuy={(qty) => handleBuy(raffle.id, qty)}
                  isBuying={buyingId === raffle.id}
                  supply={raffle.supply}
                  ticketsSoldCount={raffle.ticketsSold}
                  ticketLimitPerWallet={raffle.ticketLimitPerWallet}
                  totalTicketValue={raffle.totalTicketValue}
                />
              ))}
            </div>
          )}

          {meta && meta.totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={meta.totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}
    </>
  );
};

export default RaffleList;
